import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import DOMPurify from "dompurify";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import {
  MessageSquare, Loader2, AlertTriangle, Clock, CheckCircle2, Users, BarChart3,
  Phone, Mail, Settings, Send, Ban, Sparkles, ChevronRight, X, Building2,
  Hourglass, Zap, Activity, TrendingUp, RefreshCw, FileEdit, Wand2, Bot
} from "lucide-react";

const STATUS_BUTTON_LABELS = {
  awaiting_data: "Richiedi Dati Cliente",
  configuring: "Avvia Configurazione",
  test_send: "Testa Invio",
  active: "Segna Attivo",
  blocked: "Sposta Bloccato",
};

const fmtRelative = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days === 0) return "oggi";
  if (days === 1) return "1g fa";
  if (days < 30) return `${days}g fa`;
  return `${Math.floor(days / 30)}m fa`;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
};

export const SuperAdminWhatsAppTab = () => {
  const [stages, setStages] = useState([]);
  const [requests, setRequests] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drawerReq, setDrawerReq] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  const fetch = async () => {
    setLoading(true);
    try {
      const [listRes, anaRes] = await Promise.all([
        axios.get(`${API}/whatsapp-requests/admin/list?period=30`, { withCredentials: true }),
        axios.get(`${API}/whatsapp-requests/admin/analytics?period=90`, { withCredentials: true }),
      ]);
      setRequests(listRes.data.requests);
      setKpis(listRes.data.kpis);
      setStages(listRes.data.stages);
      setAnalytics(anaRes.data);
    } catch {
      toast.error("Errore caricamento WhatsApp");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetch(); }, []);

  const byStage = useMemo(() => {
    const m = {};
    stages.forEach(s => { m[s.key] = []; });
    requests.forEach(r => {
      if (m[r.status]) m[r.status].push(r);
      else m[r.status] = [r];
    });
    return m;
  }, [requests, stages]);

  const patchRequest = async (request_id, body) => {
    try {
      const r = await axios.patch(`${API}/whatsapp-requests/admin/${request_id}`, body, { withCredentials: true });
      const fresh = r.data.request;
      setRequests(prev => prev.map(x => x.request_id === request_id ? { ...x, ...fresh, days_open: x.days_open } : x));
      if (drawerReq?.request_id === request_id) setDrawerReq(prev => ({ ...prev, ...fresh }));
      // Refresh KPI silently
      fetch();
      return fresh;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
      return null;
    }
  };

  if (loading && !requests.length) {
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="sa-whatsapp-tab">
      {/* KPI cards */}
      {kpis && <KpiGrid kpis={kpis} />}

      {/* Pipeline Kanban */}
      <div className="rounded-3xl bg-card border p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-bold text-base flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-emerald-500" />
            Pipeline Attivazioni
          </h2>
          <button onClick={fetch} className="text-xs px-3 py-1 rounded-full bg-muted hover:bg-muted/70 flex items-center gap-1" data-testid="sa-wa-refresh">
            <RefreshCw className="w-3 h-3" /> Aggiorna
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3 overflow-x-auto" data-testid="sa-wa-kanban">
          {stages.map(stage => (
            <div key={stage.key} className="rounded-2xl p-3 bg-muted/40 border" data-testid={`sa-wa-col-${stage.key}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <span className={`w-2 h-2 rounded-full ${stage.color}`} />
                  <span className="text-xs font-semibold">{stage.label}</span>
                </div>
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-background border">{(byStage[stage.key] || []).length}</span>
              </div>
              <div className="space-y-2 min-h-[120px]">
                {(byStage[stage.key] || []).map(r => (
                  <RequestCard key={r.request_id} req={r} onOpen={() => setDrawerReq(r)} />
                ))}
                {(byStage[stage.key] || []).length === 0 && (
                  <p className="text-[10px] text-muted-foreground text-center py-4">Vuoto</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Analytics */}
      {analytics && <AnalyticsPanel analytics={analytics} />}

      {/* Drawer */}
      <RequestDrawer req={drawerReq} onClose={() => setDrawerReq(null)} onPatch={patchRequest} stages={stages} />
    </div>
  );
};

// ============ KPI Grid ============
const KpiGrid = ({ kpis }) => {
  const cards = [
    { key: "open_requests", label: "Richieste Aperte", value: kpis.open_requests, icon: Hourglass, gradient: "from-amber-500 to-orange-600" },
    { key: "active_total", label: "WhatsApp Attivi", value: kpis.active_total, icon: CheckCircle2, gradient: "from-emerald-500 to-teal-600" },
    { key: "awaiting_data", label: "In Attesa Dati", value: kpis.awaiting_data, icon: Clock, gradient: "from-yellow-500 to-amber-600" },
    { key: "configuring", label: "In Configurazione", value: kpis.configuring, icon: Settings, gradient: "from-blue-500 to-indigo-600" },
    { key: "avg_activation_hours", label: "Tempo Medio (h)", value: kpis.avg_activation_hours.toFixed(1), icon: Zap, gradient: "from-violet-500 to-purple-600" },
    { key: "recent_active_period", label: "Attivati 30gg", value: kpis.recent_active_period, icon: TrendingUp, gradient: "from-pink-500 to-rose-600" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="sa-wa-kpi-grid">
      {cards.map(c => (
        <div key={c.key} className={`rounded-2xl p-4 bg-gradient-to-br ${c.gradient} text-white shadow hover:shadow-lg transition-shadow relative overflow-hidden`} data-testid={`sa-wa-kpi-${c.key}`}>
          <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-white/10 blur-xl pointer-events-none" />
          <div className="relative">
            <c.icon className="w-4 h-4 opacity-80 mb-2" />
            <p className="text-2xl font-bold">{c.value}</p>
            <p className="text-[10px] uppercase tracking-wider opacity-80 mt-0.5">{c.label}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

// ============ Request Card ============
const RequestCard = ({ req, onOpen }) => (
  <button
    type="button"
    onClick={onOpen}
    className="w-full text-left p-3 rounded-xl bg-background border-2 hover:shadow-md hover:border-emerald-500/40 transition-all relative"
    data-testid={`sa-wa-card-${req.request_id}`}
  >
    {req.is_alert && (
      <span className="absolute -top-1.5 -right-1.5 px-1.5 py-0.5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center gap-0.5 shadow">
        <AlertTriangle className="w-2.5 h-2.5" /> ALERT
      </span>
    )}
    {req.is_urgent && !req.is_alert && (
      <span className="absolute -top-1.5 -right-1.5 px-1.5 py-0.5 rounded-full bg-orange-500 text-white text-[9px] font-bold shadow" data-testid={`sa-wa-urgent-${req.request_id}`}>
        URGENTE
      </span>
    )}
    <p className="font-semibold text-sm truncate">{req.business_name}</p>
    <div className="flex items-center gap-1 text-[10px] text-muted-foreground mt-0.5">
      <Phone className="w-2.5 h-2.5" />
      <span className="truncate">{req.phone}</span>
    </div>
    {req.email && (
      <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
        <Mail className="w-2.5 h-2.5" />
        <span className="truncate">{req.email}</span>
      </div>
    )}
    <div className="flex items-center gap-1.5 mt-2 flex-wrap">
      {req.subscription_tier && (
        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-violet-100 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300 font-medium uppercase">
          {req.subscription_tier}
        </span>
      )}
      <span className="text-[9px] text-muted-foreground">{req.days_open}g · {fmtRelative(req.created_at)}</span>
    </div>
  </button>
);

// ============ Drawer ============
const RequestDrawer = ({ req, onClose, onPatch, stages }) => {
  const [notes, setNotes] = useState("");
  const [config, setConfig] = useState({ phone_number_id: "", access_token: "", waba_id: "", webhook_status: "" });
  const [savingNotes, setSavingNotes] = useState(false);

  useEffect(() => {
    if (req) {
      setNotes(req.notes || "");
      setConfig({
        phone_number_id: req.phone_number_id || "",
        access_token: req.access_token || "",
        waba_id: req.waba_id || "",
        webhook_status: req.webhook_status || "",
      });
    }
  }, [req?.request_id]);

  if (!req) return null;

  const moveTo = async (status) => {
    await onPatch(req.request_id, { status });
    toast.success(`Spostato in ${stages.find(s => s.key === status)?.label || status}`);
  };

  const toggleChecklist = async (key) => {
    const next = (req.checklist || []).map(c => c.key === key ? { ...c, checked: !c.checked } : c);
    await onPatch(req.request_id, { checklist: next });
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    await onPatch(req.request_id, { notes });
    setSavingNotes(false);
    toast.success("Note salvate");
  };

  const saveConfig = async () => {
    await onPatch(req.request_id, config);
    toast.success("Configurazione salvata");
  };

  return (
    <Dialog open={!!req} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl p-0 overflow-hidden gap-0 max-h-[90vh] flex flex-col" data-testid="sa-wa-drawer">
        {/* Header */}
        <div className="bg-gradient-to-br from-emerald-500 via-green-500 to-teal-600 text-white p-5 relative">
          <button onClick={onClose} className="absolute right-4 top-4 rounded-full p-1.5 bg-white/15 hover:bg-white/25 transition-colors" data-testid="sa-wa-drawer-close">
            <X className="w-4 h-4" />
          </button>
          <DialogHeader>
            <DialogTitle className="text-lg pr-10 flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              {req.business_name}
            </DialogTitle>
            <p className="text-xs opacity-80 mt-0.5">Status: <strong>{stages.find(s => s.key === req.status)?.label || req.status}</strong> · {req.days_open}g aperta</p>
          </DialogHeader>
        </div>

        {/* Body scrollable */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Quick CTAs */}
          <div className="flex flex-wrap gap-2" data-testid="sa-wa-cta-row">
            {["awaiting_data", "configuring", "test_send", "active", "blocked"].map(s => (
              <Button
                key={s}
                size="sm"
                variant={s === "blocked" ? "outline" : "default"}
                onClick={() => moveTo(s)}
                disabled={req.status === s}
                className={`rounded-full text-xs ${
                  s === "active" ? "bg-emerald-500 hover:bg-emerald-600 text-white" :
                  s === "blocked" ? "border-red-500 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30" :
                  ""
                }`}
                data-testid={`sa-wa-cta-${s}`}
              >
                {s === "active" && <CheckCircle2 className="w-3 h-3 mr-1" />}
                {s === "blocked" && <Ban className="w-3 h-3 mr-1" />}
                {s === "test_send" && <Send className="w-3 h-3 mr-1" />}
                {s === "configuring" && <Settings className="w-3 h-3 mr-1" />}
                {s === "awaiting_data" && <Mail className="w-3 h-3 mr-1" />}
                {STATUS_BUTTON_LABELS[s]}
              </Button>
            ))}
          </div>

          {/* Client data */}
          <Section title="Dati Cliente" icon={Users}>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Field label="Ragione sociale" value={req.business_name} />
              <Field label="Email" value={req.email} mono />
              <Field label="Telefono" value={req.phone} mono />
              <Field label="Piano" value={req.subscription_tier?.toUpperCase() || "—"} />
              <Field label="Settore" value={req.sector || "—"} />
              <Field label="Numero" value={req.current_or_new_number === "current" ? "Attuale" : "Nuovo"} />
            </div>
            {(req.features_requested || []).length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {req.features_requested.map(f => (
                  <span key={f} className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 font-medium">{f}</span>
                ))}
              </div>
            )}
          </Section>

          {/* Tech config */}
          <Section title="Configurazione Tecnica" icon={Settings}>
            <div className="space-y-2">
              <Input placeholder="Phone Number ID" value={config.phone_number_id} onChange={(e) => setConfig({ ...config, phone_number_id: e.target.value })} className="font-mono text-xs" data-testid="sa-wa-cfg-phone-id" />
              <Input placeholder="Access Token" value={config.access_token} onChange={(e) => setConfig({ ...config, access_token: e.target.value })} className="font-mono text-xs" data-testid="sa-wa-cfg-token" />
              <Input placeholder="WABA ID" value={config.waba_id} onChange={(e) => setConfig({ ...config, waba_id: e.target.value })} className="font-mono text-xs" data-testid="sa-wa-cfg-waba" />
              <Input placeholder="Webhook status (es. verified)" value={config.webhook_status} onChange={(e) => setConfig({ ...config, webhook_status: e.target.value })} className="text-xs" data-testid="sa-wa-cfg-webhook" />
              <Button size="sm" onClick={saveConfig} className="rounded-full bg-blue-500 hover:bg-blue-600 text-white" data-testid="sa-wa-cfg-save">Salva configurazione</Button>
              {req.last_webhook_test_at && (
                <p className="text-[10px] text-muted-foreground">Ultimo test webhook: {fmtDate(req.last_webhook_test_at)}</p>
              )}
            </div>
          </Section>

          {/* Checklist */}
          <Section title="Checklist Setup" icon={CheckCircle2}>
            <div className="space-y-1.5">
              {(req.checklist || []).map(c => (
                <label key={c.key} className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-muted/40 cursor-pointer" data-testid={`sa-wa-check-${c.key}`}>
                  <input
                    type="checkbox"
                    checked={!!c.checked}
                    onChange={() => toggleChecklist(c.key)}
                    className="w-4 h-4 accent-emerald-500"
                  />
                  <span className={`text-sm ${c.checked ? "line-through text-muted-foreground" : ""}`}>{c.label}</span>
                </label>
              ))}
            </div>
          </Section>

          {/* Notes */}
          <Section title="Note Interne" icon={FileEdit}>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full text-sm rounded-lg border p-2.5 resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
              placeholder="Note interne (visibili solo al super admin)..."
              data-testid="sa-wa-notes"
            />
            <Button size="sm" onClick={saveNotes} disabled={savingNotes} className="rounded-full mt-2 bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="sa-wa-notes-save">
              {savingNotes ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />}
              Salva note
            </Button>
          </Section>

          {/* AI Copilot */}
          <CopilotPanel requestId={req.request_id} />

          {/* Stage history */}
          {(req.stage_history || []).length > 0 && (
            <Section title="Cronologia" icon={Activity}>
              <div className="space-y-1.5">
                {req.stage_history.slice().reverse().slice(0, 8).map((h, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <ChevronRight className="w-3 h-3 text-muted-foreground" />
                    <span className="font-medium">{h.to}</span>
                    <span className="text-muted-foreground">· {fmtDate(h.at)}</span>
                  </div>
                ))}
              </div>
            </Section>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Section = ({ title, icon: Icon, children }) => (
  <div className="rounded-2xl border bg-background p-4">
    <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
      <Icon className="w-4 h-4 text-emerald-500" />
      {title}
    </h3>
    {children}
  </div>
);

const Field = ({ label, value, mono }) => (
  <div>
    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className={`text-sm font-medium ${mono ? "font-mono text-xs" : ""} truncate`}>{value || "—"}</p>
  </div>
);

// ============ AI Copilot Panel ============
const PRIORITY_COLOR = {
  high: "bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/30",
  medium: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30",
  low: "bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/30",
};

const CopilotPanel = ({ requestId }) => {
  const [suggestion, setSuggestion] = useState(null);
  const [loadingSug, setLoadingSug] = useState(false);
  const [draft, setDraft] = useState(null); // {subject, body, to}
  const [loadingDraft, setLoadingDraft] = useState(false);
  const [editing, setEditing] = useState(false);
  const [sending, setSending] = useState(false);

  const getSuggestion = async () => {
    setLoadingSug(true);
    try {
      const r = await axios.post(`${API}/whatsapp-requests/admin/${requestId}/copilot/suggest`, {}, { withCredentials: true });
      setSuggestion(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI non disponibile");
    } finally {
      setLoadingSug(false);
    }
  };

  const getDraft = async () => {
    setLoadingDraft(true);
    try {
      const r = await axios.post(`${API}/whatsapp-requests/admin/${requestId}/copilot/email-draft`, {}, { withCredentials: true });
      setDraft(r.data);
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI non disponibile");
    } finally {
      setLoadingDraft(false);
    }
  };

  const sendEmail = async () => {
    if (!draft) return;
    setSending(true);
    try {
      const r = await axios.post(
        `${API}/whatsapp-requests/admin/${requestId}/copilot/email-send`,
        { subject: draft.subject, body: draft.body, to: draft.to },
        { withCredentials: true }
      );
      if (r.data.success) {
        toast.success("Email inviata");
      } else {
        toast.warning(r.data.note || "Email non inviata");
      }
      setDraft(null);
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="rounded-2xl border-2 bg-gradient-to-br from-violet-50 via-fuchsia-50 to-pink-50 dark:from-violet-950/30 dark:via-fuchsia-950/30 dark:to-pink-950/30 border-violet-200 dark:border-violet-800/50 p-4" data-testid="sa-wa-copilot">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-sm flex items-center gap-2">
          <div className="w-7 h-7 rounded-xl bg-gradient-to-br from-violet-500 via-fuchsia-500 to-pink-500 flex items-center justify-center shadow-sm">
            <Bot className="w-4 h-4 text-white" />
          </div>
          AI Copilot
          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-violet-500/20 text-violet-700 dark:text-violet-300 uppercase tracking-wider">Beta</span>
        </h3>
      </div>

      {/* Suggestion */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
            <Wand2 className="w-3 h-3" /> Prossima azione
          </p>
          <Button
            size="sm"
            variant="outline"
            onClick={getSuggestion}
            disabled={loadingSug}
            className="h-7 rounded-full text-xs border-violet-500/30 hover:bg-violet-500/10"
            data-testid="sa-wa-copilot-suggest"
          >
            {loadingSug ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
            {suggestion ? "Rigenera" : "Suggerisci"}
          </Button>
        </div>
        {suggestion && (
          <div className="rounded-xl bg-white/60 dark:bg-black/20 backdrop-blur p-3 border border-white/50 dark:border-white/5" data-testid="sa-wa-copilot-suggestion">
            <div className="flex items-start gap-2 mb-1.5">
              <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${PRIORITY_COLOR[suggestion.priority] || PRIORITY_COLOR.medium}`}>
                {suggestion.priority}
              </span>
              {suggestion.client_outreach_needed && (
                <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30">
                  ✉️ Scrivi al cliente
                </span>
              )}
            </div>
            <p className="text-sm font-semibold mb-1 text-foreground">{suggestion.next_action}</p>
            {suggestion.reason && <p className="text-xs text-muted-foreground italic">→ {suggestion.reason}</p>}
          </div>
        )}
      </div>

      {/* Email draft */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
            <Mail className="w-3 h-3" /> Auto-email
          </p>
          <Button
            size="sm"
            variant="outline"
            onClick={getDraft}
            disabled={loadingDraft}
            className="h-7 rounded-full text-xs border-violet-500/30 hover:bg-violet-500/10"
            data-testid="sa-wa-copilot-draft"
          >
            {loadingDraft ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
            {draft ? "Rigenera" : "Genera bozza"}
          </Button>
        </div>
        {draft && (
          <div className="rounded-xl bg-white/60 dark:bg-black/20 backdrop-blur p-3 border border-white/50 dark:border-white/5 space-y-2" data-testid="sa-wa-copilot-draft-panel">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">A:</p>
              {editing ? (
                <Input value={draft.to || ""} onChange={(e) => setDraft({ ...draft, to: e.target.value })} className="h-8 text-xs" />
              ) : (
                <p className="text-xs font-mono">{draft.to}</p>
              )}
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">Oggetto:</p>
              {editing ? (
                <Input value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} className="h-8 text-sm font-semibold" />
              ) : (
                <p className="text-sm font-bold">{draft.subject}</p>
              )}
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">Corpo:</p>
              {editing ? (
                <textarea
                  value={draft.body}
                  onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                  rows={6}
                  className="w-full text-xs rounded-lg border p-2 resize-y font-mono"
                  data-testid="sa-wa-copilot-body-edit"
                />
              ) : (
                <div
                  className="text-sm prose prose-sm max-w-none dark:prose-invert"
                  data-testid="sa-wa-copilot-body-preview"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(draft.body || "", {
                      USE_PROFILES: { html: true },
                      FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "form"],
                      FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover", "onfocus", "onblur"],
                    }),
                  }}
                />
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button size="sm" variant="ghost" onClick={() => setEditing(!editing)} className="rounded-full text-xs" data-testid="sa-wa-copilot-edit">
                {editing ? "Anteprima" : "Modifica"}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setDraft(null)} className="rounded-full text-xs">Annulla</Button>
              <Button
                size="sm"
                onClick={sendEmail}
                disabled={sending}
                className="rounded-full text-xs bg-gradient-to-r from-violet-500 to-pink-500 hover:from-violet-600 hover:to-pink-600 text-white"
                data-testid="sa-wa-copilot-send"
              >
                {sending ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                Invia al cliente
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============ Analytics ============
const AnalyticsPanel = ({ analytics }) => {
  const maxReq = Math.max(1, ...(analytics.requests_by_month || []).map(r => r.count));
  const maxAct = Math.max(1, ...(analytics.activations_by_month || []).map(r => r.count));
  return (
    <div className="rounded-3xl bg-card border p-5" data-testid="sa-wa-analytics">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-bold text-base flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-violet-500" /> Analytics
        </h2>
        <span className="text-[10px] text-muted-foreground">Ultimi {analytics.period_days}gg</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Mini label="Richieste" value={analytics.total_requests} />
        <Mini label="Attivazioni" value={analytics.total_active} />
        <Mini label="Conversion" value={`${analytics.conversion_rate}%`} accent="text-emerald-600" />
        <Mini label="Stuck >7gg" value={analytics.stuck_count} accent={analytics.stuck_count > 0 ? "text-red-600" : ""} />
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <BarChart title="Richieste / mese" data={analytics.requests_by_month} max={maxReq} color="bg-blue-500" />
        <BarChart title="Attivazioni / mese" data={analytics.activations_by_month} max={maxAct} color="bg-emerald-500" />
      </div>

      {(analytics.active_by_sector || []).length > 0 && (
        <div className="mt-5">
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Clienti attivi per settore</p>
          <div className="flex flex-wrap gap-2">
            {analytics.active_by_sector.map(s => (
              <span key={s.sector} className="text-xs px-3 py-1 rounded-full bg-muted font-medium">
                {s.sector} · <span className="text-emerald-600 font-bold">{s.count}</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const Mini = ({ label, value, accent = "" }) => (
  <div className="p-3 rounded-2xl bg-muted/40 border">
    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className={`text-xl font-bold ${accent}`}>{value}</p>
  </div>
);

const BarChart = ({ title, data, max, color }) => (
  <div>
    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">{title}</p>
    {!data?.length ? (
      <p className="text-xs text-muted-foreground italic">Nessun dato nel periodo</p>
    ) : (
      <div className="space-y-1.5">
        {data.map(d => (
          <div key={d.month} className="flex items-center gap-2">
            <span className="text-[10px] w-14 text-muted-foreground font-mono">{d.month}</span>
            <div className="flex-1 h-5 rounded-full bg-muted/50 overflow-hidden">
              <div className={`h-full ${color} flex items-center justify-end px-1.5 text-[9px] font-bold text-white transition-all`} style={{ width: `${(d.count / max) * 100}%` }}>
                {d.count > 0 && d.count}
              </div>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

export default SuperAdminWhatsAppTab;
